import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Input, Spin, message } from "antd";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";


/**
 * EditCompanyModal
 * Props:
 * - visible: boolean
 * - onClose: function
 * - onSave: function (companyObj)
 * - fetchCwompanies: function (returns Promise<companyList>)
 * - addCompany: function (companyName) => Promise<companyObj>
 * - initialCompany: string | null
 */
const EditCompanyModal = ({
  visible,
  onClose,
  onSave,
  fetchCompanies,
  addCompany,
  initialCompanyName,
  initialCompanyId,
  initialCompanyBookingId,
}) => {
  const [companies, setCompanies] = useState([]);
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  // selectedCompany is the company id (partyId) or null
  const [selectedCompany, setSelectedCompany] = useState(initialCompanyId ?? null);
  const [searchText, setSearchText] = useState("");
  const [adding, setAdding] = useState(false);
  const [newCompany, setNewCompany] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [companyBookingId, setCompanyBookingId] = useState(initialCompanyBookingId || "");

  useEffect(() => {
    if (visible) {
      setLoading(true);
      fetchCompanies("")
        .then((list) => {
          let companiesList = list;
          // If initialCompany is set and not in the list, add it
          if (initialCompanyId !== undefined && initialCompanyId !== null) {
            const alreadyInList = list.some((c) => c.id === initialCompanyId);
            if (!alreadyInList) {
              // Try to find the company in previous state (if any)
              let extraCompany = null;
              if (companies.length > 0) {
                extraCompany = companies.find((c) => c.id === initialCompanyId);
              }
              // If not found, create a placeholder
              if (!extraCompany) {
                extraCompany = { id: initialCompanyId, displayName: searchText || initialCompanyName || "WalkIn" };
              }
              companiesList = [...list, extraCompany];
            }
          }
          setCompanies(companiesList);
          // Set searchText to displayName of initialCompany (partyId) if present, only on open
          if (initialCompanyId !== undefined && initialCompanyId !== null) {
            const company = companiesList.find((c) => c.id === initialCompanyId);
            setSearchText(company ? company.displayName : "");
            setSelectedCompany(initialCompanyId ?? null);
          } else {
            setSearchText("");
            setSelectedCompany(null);
          }
        })
        .catch(() => message.error("Failed to fetch companies"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, [visible, initialCompanyId]);

  // Handle search
  const handleSearch = async (value) => {
    setSearchText(value);
    setLoading(true);
    try {
      const list = await fetchCompanies(value);
      setCompanies(list);
      // Focus the input after search completes
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } catch {
      message.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompany.trim()) {
      message.warning("Please enter a company name");
      return;
    }
    setAddLoading(true);
    try {
      const company = await addCompany(newCompany.trim());
      setCompanies((prev) => [...prev, company]);
      setSelectedCompany(company.name);
      setAdding(false);
      setNewCompany("");
      message.success("Company added");
    } catch (e) {
      message.error("Failed to add company");
    } finally {
      setAddLoading(false);
    }
  };

  const handleSave = () => {
    // If selectedCompany is null, treat as WalkIn (no company)
    if (!selectedCompany) {
      onSave({ id: null, displayName: 'WalkIn', companyBookingId });
      return;
    }
    const companyObj = companies.find((c) => c.id === selectedCompany);
    if (!companyObj) {
      // fallback: treat as WalkIn
      onSave({ id: null, displayName: 'WalkIn', companyBookingId });
      return;
    }
    onSave({ ...companyObj, companyBookingId });
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title="Edit Company"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <div className="flex justify-center items-center py-6">
          <Spin />
        </div>
      ) : (
        <>
          <div className="mb-4">
            <label className="block mb-2 font-medium">Select Company</label>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.displayName || ""}
              loading={loading}
              value={companies.find((c) => c.id === selectedCompany) || null}
              inputValue={searchText}
              openOnFocus
              autoHighlight
              open={undefined} // Let Autocomplete manage open state
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "input") {
                  setSearchText(newInputValue);
                  handleSearch(newInputValue);
                }
              }}
              onChange={(_, newValue) => {
                setSelectedCompany(newValue ? newValue.id : null);
                setSearchText(newValue ? newValue.displayName : "");
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  label="Select Company"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <Spin size="small" /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.displayName}
                </li>
              )}
              freeSolo={false}
              style={{ width: "100%" }}
            />
            {/* Add new company UI above Company Booking ID */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 8, marginTop: 8 }}>
              {adding ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    value={newCompany}
                    onChange={e => setNewCompany(e.target.value)}
                    placeholder="New company name"
                    onPressEnter={handleAddCompany}
                    disabled={addLoading}
                  />
                  <Button type="primary" loading={addLoading} onClick={handleAddCompany}>
                    Add
                  </Button>
                  <Button onClick={() => setAdding(false)} disabled={addLoading}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button type="link" onClick={() => setAdding(true)} style={{ padding: 0 }}>
                  + Add new company
                </Button>
              )}
            </div>
            {/* Company Booking ID input */}
            <div style={{ marginTop: 16 }}>
              <label className="block mb-2 font-medium">Company Booking ID</label>
              <Input
                value={companyBookingId}
                onChange={e => setCompanyBookingId(e.target.value)}
                placeholder="Enter company booking id (optional)"
                maxLength={64}
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditCompanyModal;
